import { getServerRuntimeConfig } from '@/config/runtime/serverRuntimeConfig';
import { isServer } from '@/services/Utilities/Runtime';
import type { Logger } from 'winston';
import type { ILogger } from './loggerBase';
import { LOG_ORIGIN_FIELD, LogLevel, LogOrigin } from './loggerBase';

/*
 * ERROR FORMATTER.
 * We cannot easily stringify errors because their important properties are not enumerable.
 */
const jsonErrorReplacer = (key: string, value: any) => {
    if (value instanceof Error) {
        return {
            // Pull all enumerable properties, supporting properties on custom Errors
            ...value,
            // Explicitly pull Error's non-enumerable properties
            name: value.name,
            message: value.message,
            stack: value.stack,
        };
    }
    return value;
};

const capitalizeFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/*
 * Prepare the target string to be a property of an object.
 */
const prepareString = (s: any): string => {
    if (typeof s != 'string') {
        return prepareString(typeof s);
    }
    s = s.replace(/[""]/g, '\\"');
    s = s.replace(/(?:\\r\\n|\\r|\\n)/g, '');
    return s.trim();
};

/*
 * GUTS Friendly formatter.
 * It extracts the context into separate keys.
 * level="log level" message="some message" ctxKey1="value1" ctxKey2="value2" ...
 * If the timestamp is available, it will also be included
 */
export const getGUTSFriendlyFormatter = ({ timestamp, level, message, ...meta }: any) => {
    meta = meta || {};

    if (!Object.keys(meta).includes(LOG_ORIGIN_FIELD)) {
        meta[LOG_ORIGIN_FIELD] = isServer ? LogOrigin.Server : LogOrigin.Client;
    }

    const transformedContext = Object.keys(meta).map((key) => {
        // Due to stringify adding quotes to regular strings, we can skip it for this type.
        const parsedElement = typeof meta[key] === 'string' ? meta[key] : JSON.stringify(meta[key], jsonErrorReplacer);
        return `ctx${capitalizeFirst(key)}="${prepareString(parsedElement)}"`;
    });
    const format = `level="${level}" message="[[[ ${prepareString(message)} ]]]" ctxDfeBuildId="${
        getServerRuntimeConfig().clientConfig.buildId
    }" ${transformedContext.join(' ')}`;
    return timestamp ? `${timestamp} ${format}` : format;
};

/*
 * Return the minimum acceptable log level to record in this environment.
 */
export const getMinLogLevel = () => {
    let logLevel;
    if (isServer) {
        logLevel = getServerRuntimeConfig().logLevel;
    }
    return logLevel || LogLevel.Info;
};

/*
 * Create a server-side logger to record server errors.
 */
let logger: Logger;
const getServerLogger = () => {
    if (!logger) {
        const winston = require('winston'); // so that it is not bundled
        const cliFormat =
            getServerRuntimeConfig().clientConfig.environment === 'local'
                ? winston.format.cli()
                : winston.format.simple();
        logger = winston.createLogger({
            level: getMinLogLevel(),
            format: winston.format.combine(
                cliFormat,
                winston.format.timestamp(),
                winston.format.splat(),
                winston.format.printf(getGUTSFriendlyFormatter)
            ),
            transports: [new winston.transports.Console()],
        });
    }
    return logger;
};

const serverLogger: ILogger = {
    error: (message: string, context: Record<string, any> = {}) => {
        getServerLogger().error(message, context);
    },
    warn: (message: string, context?: Record<string, any>) => {
        getServerLogger().warn(message, context);
    },
    info: (message: string, context?: Record<string, any>) => {
        getServerLogger().info(message, context);
    },
    log: (level: LogLevel, message: string, context?: Record<string, any>) => {
        getServerLogger().log(level, message, context);
    },
};

export default serverLogger;
